'use client';

import { useEffect } from 'react';
import Link from 'next/link';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ArrowLeft, BookOpen } from 'lucide-react';
import { HELP_MARKDOWN, HELP_SECTIONS, HEADING_ID_MAP } from '@/content/dingtalk-help/guide';

function extractText(node: unknown): string {
  if (typeof node === 'string') return node;
  if (typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(extractText).join('');
  if (node && typeof node === 'object' && 'props' in node) {
    const props = (node as { props?: { children?: unknown } }).props;
    return extractText(props?.children);
  }
  return '';
}

export default function DingtalkHelpPage() {
  useEffect(() => {
    const hash = typeof window !== 'undefined' ? window.location.hash : '';
    if (hash) {
      const id = hash.replace('#', '');
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 200);
    }
  }, []);

  return (
    <div className="flex h-full" style={{ backgroundColor: '#f7f8fa' }}>
      <aside
        className="w-56 shrink-0 border-r bg-white overflow-auto"
        style={{ borderColor: '#e5e6eb' }}
      >
        <div className="p-4 border-b" style={{ borderColor: '#e5e6eb' }}>
          <Link
            href="/sync-center/dingtalk"
            className="inline-flex items-center gap-1 text-xs hover:underline"
            style={{ color: '#3370ff' }}
          >
            <ArrowLeft className="w-3 h-3" />
            返回同步中心
          </Link>
          <div className="flex items-center gap-2 mt-3">
            <BookOpen className="w-4 h-4" style={{ color: '#3370ff' }} />
            <span className="text-sm font-semibold">使用说明</span>
          </div>
        </div>
        <nav className="p-2">
          {HELP_SECTIONS.map((s) => (
            <a
              key={s.id}
              href={`#${s.id}`}
              className="block px-3 py-2 text-xs rounded hover:bg-gray-100"
              style={{ color: '#646a73' }}
            >
              {s.title}
            </a>
          ))}
        </nav>
      </aside>

      <main className="flex-1 overflow-auto">
        <div className="max-w-3xl mx-auto px-8 py-8">
          <article className="help-markdown">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                h2: ({ children, ...props }) => {
                  const text = extractText(children).trim();
                  const id = HEADING_ID_MAP[text];
                  return (
                    <h2 id={id} {...props}>
                      {children}
                    </h2>
                  );
                },
              }}
            >
              {HELP_MARKDOWN}
            </ReactMarkdown>
          </article>
        </div>
      </main>

      <style jsx global>{`
        .help-markdown { color: #1f2329; font-size: 14px; line-height: 1.7; word-break: break-word; }
        .help-markdown h1 { font-size: 24px; font-weight: 700; margin: 0 0 20px; }
        .help-markdown h2 { font-size: 20px; font-weight: 600; margin: 32px 0 14px; padding-top: 8px; border-top: 1px solid #e5e6eb; scroll-margin-top: 16px; }
        .help-markdown h2:first-of-type { border-top: none; padding-top: 0; }
        .help-markdown h3 { font-size: 16px; font-weight: 600; margin: 24px 0 10px; color: #1f2329; }
        .help-markdown h4 { font-size: 14px; font-weight: 600; margin: 18px 0 8px; }
        .help-markdown p { margin: 10px 0; }
        .help-markdown ul, .help-markdown ol { margin: 10px 0; padding-left: 22px; }
        .help-markdown li { margin: 4px 0; }
        .help-markdown code { background: #f5f6f7; color: #d4380d; padding: 2px 6px; border-radius: 3px; font-size: 12.5px; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
        .help-markdown pre { background: #f5f6f7; padding: 12px; border-radius: 6px; overflow: auto; margin: 12px 0; }
        .help-markdown pre code { background: none; color: #1f2329; padding: 0; font-size: 12.5px; white-space: pre; }
        .help-markdown table { border-collapse: collapse; width: 100%; margin: 12px 0; font-size: 13px; }
        .help-markdown th, .help-markdown td { border: 1px solid #e5e6eb; padding: 8px 10px; text-align: left; vertical-align: top; }
        .help-markdown th { background: #fafbfc; font-weight: 600; }
        .help-markdown hr { border: none; border-top: 1px solid #e5e6eb; margin: 28px 0; }
        .help-markdown a { color: #3370ff; text-decoration: none; }
        .help-markdown a:hover { text-decoration: underline; }
        .help-markdown strong { color: #1f2329; }
      `}</style>
    </div>
  );
}
